import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { toggleSubscribeAsync } from "../../services/account-service";
import { IForm, initForm } from "../molecules/form";
import { getParameter } from "../../utils/querystring";
import { writeCookieThatExpiresAtTheEndOfTheDay } from "../../utils/cookie";
import { trackAccountPrefEvent } from "../../common/events";

export enum IMailFrequency {
	None = 0,
	Weekly = 1,
	BiWeekly = 2,
	Monthly = 4,
}

export interface IEmailPreferences {
	readonly dispose: () => void;
}

export interface ICreateEmailPreferenceOptions {
	defaultMailFrequency: IMailFrequency;
	onSubmit?: () => void;
	trackEvent?: boolean;
	hasUnsubscribed?: () => boolean;
}

export function createEmailPreferences(containerEl: HTMLElement, opts: ICreateEmailPreferenceOptions) {
	const formEl =
		containerEl instanceof HTMLFormElement
			? containerEl
			: expressQuerySelector<HTMLFormElement>(containerEl, "form", false);
	const emailEl = expressQuerySelector<HTMLInputElement>(containerEl, ".technical-preferences-mail", false);
	const personalMessageEl = expressQuerySelector<HTMLInputElement>(containerEl, "#personalmessage", false);
	const newsletterEl = expressQuerySelector<HTMLInputElement>(containerEl, "#newsletter", false);
	const mailFrequency = expressQuerySelectorAll<HTMLInputElement>(containerEl, '[name="Frequency"]');
	const postalPreferencesEl = expressQuerySelector<HTMLInputElement>(containerEl, "#PostalPreferences", false);
	const emptyGuid = "00000000-0000-0000-0000-000000000000";
	const { defaultMailFrequency, onSubmit } = opts;
	const email = getParameter("email");
	let emailPreferencesForm: IForm | null = null;
	if (email && emailEl) emailEl.value = email;

	let origEmailFrequencyValue = +mailFrequency?.find((x) => x.checked && !x.disabled)?.dataset.value || 0;
	let origNewsletterValue = newsletterEl && newsletterEl.checked;
	let origPostalPreferencesValue = postalPreferencesEl && postalPreferencesEl.checked;
	let origPersonalMessageValue = personalMessageEl && personalMessageEl.checked;

	const onCheckboxChange = (ev: MouseEvent) => {
		const el = ev.target as HTMLInputElement;
		if (el.id !== newsletterEl?.id && el.id !== personalMessageEl?.id) return;

		const disableSubscribeAttributes = !newsletterEl?.checked && !personalMessageEl?.checked;
		const valueSelected = mailFrequency.find((x) => x.checked)?.dataset.value;
		mailFrequency.forEach((x) => {
			x.closest(".a-form-input").classList.toggle("a-form-input--disabled", disableSubscribeAttributes);

			x.disabled = disableSubscribeAttributes;

			// when nothing selected => check default
			if (!valueSelected) x.checked = !x.disabled && defaultMailFrequency + "" === x.dataset.value;

			if (disableSubscribeAttributes) x.checked = false;
		});
	};

	const submit = async () => {
		const mailFrequencyValue = +mailFrequency?.find((x) => x.checked && !x.disabled)?.dataset.value || 0;
		const postalPreferencesValue =
			(postalPreferencesEl && !postalPreferencesEl.disabled && postalPreferencesEl.checked) || false;

		const personId =
			containerEl.dataset.personid || personalMessageEl?.dataset.personid || newsletterEl?.dataset.personid || "";
		const email =
			emailEl?.value ||
			containerEl.dataset.email ||
			personalMessageEl?.dataset.email ||
			newsletterEl?.dataset.email ||
			"";

		if (opts.trackEvent) {
			if (
				(newsletterEl && newsletterEl.checked !== origNewsletterValue) ||
				(opts.hasUnsubscribed && opts.hasUnsubscribed())
			) {
				trackAccountPrefEvent(newsletterEl.checked ? "Subscribe to newsletter" : "Unsubscribe from newsletter", {
					page: window.context.pageName,
				});
				origNewsletterValue = newsletterEl.checked;
			}
			if (personalMessageEl && personalMessageEl.checked !== origPersonalMessageValue) {
				trackAccountPrefEvent(
					personalMessageEl.checked ? "Subscribe to personal message" : "Unsubscribe from personal message",
					{ page: window.context.pageName },
				);
				origPersonalMessageValue = personalMessageEl.checked;
			}
			if (postalPreferencesEl && postalPreferencesEl.checked !== origPostalPreferencesValue) {
				trackAccountPrefEvent(postalPreferencesEl.checked ? "Optin for postal mail" : "Optout for postal mail", {
					page: window.context.pageName,
				});
				origPostalPreferencesValue = postalPreferencesEl.checked;
			}
			if (
				(mailFrequency && mailFrequencyValue && mailFrequencyValue !== origEmailFrequencyValue) ||
				(opts.hasUnsubscribed && opts.hasUnsubscribed())
			) {
				let message: string;
				if (opts.hasUnsubscribed && opts.hasUnsubscribed()) {
					if (mailFrequencyValue === origEmailFrequencyValue) {
						if (defaultMailFrequency && origEmailFrequencyValue === defaultMailFrequency)
							message = "Changed email frequency to default " + getEmailFrequencyText(mailFrequencyValue);
						else message = "Changed email frequency to " + getEmailFrequencyText(mailFrequencyValue);
					} else {
						if (defaultMailFrequency && origEmailFrequencyValue === defaultMailFrequency)
							message =
								"Changed email frequency from default " +
								getEmailFrequencyText(origEmailFrequencyValue) +
								" to " +
								getEmailFrequencyText(mailFrequencyValue);
						else
							message =
								"Changed email frequency from " +
								getEmailFrequencyText(origEmailFrequencyValue) +
								" to " +
								getEmailFrequencyText(mailFrequencyValue);
					}
				} else {
					message =
						"Changed email frequency from " +
						getEmailFrequencyText(origEmailFrequencyValue) +
						" to " +
						getEmailFrequencyText(mailFrequencyValue);
				}

				trackAccountPrefEvent(message, { page: window.context.pageName });
				origEmailFrequencyValue = mailFrequencyValue;
			}
		}

		if (newsletterEl?.checked || personalMessageEl?.checked)
			writeCookieThatExpiresAtTheEndOfTheDay("subscribedInThisSession", "true");

		return toggleSubscribeAsync(
			personId || emptyGuid,
			email,
			window.context.appid,
			window.context.pointOfSaleId,
			window.context.language,
			window.context.pageName,
			newsletterEl?.checked,
			personalMessageEl?.checked,
			mailFrequencyValue,
			postalPreferencesEl ? postalPreferencesValue : null,
		).then((_) => {
			onSubmit && onSubmit();
		});
	};

	const getEmailFrequencyText = (value: number) => {
		switch (value) {
			case 1:
				return "weekly";
			case 2:
				return "biweekly";
			case 4:
				return "monthly";
			default:
				return "";
		}
	};

	const disposable = compositeDisposable([
		personalMessageEl && expressEventListener(personalMessageEl, "change", (ev) => onCheckboxChange(ev)),
		newsletterEl && expressEventListener(newsletterEl, "change", (ev) => onCheckboxChange(ev)),
	]);

	if (formEl) {
		emailPreferencesForm = initForm(formEl, {
			onSubmitForm: () => {
				return submit();
			},
			buttonSelector: ".technical-preferences-button",
			submitOnEnter: false,
		});
	} else {
		const buttonEl = expressQuerySelector(containerEl, ".technical-preferences-button", true);
		disposable.add(expressEventListener(buttonEl, "click", submit));
	}

	const dispose = () => {
		disposable.dispose();
		emailPreferencesForm && emailPreferencesForm.dispose();
	};

	return {
		dispose,
	} as IEmailPreferences;
}
